import { useAuth } from "@/src/contexts/auth.context";
import { useGetCategoriesQuery } from "@/src/services/api/category/category.hooks";
import {
  useNewsSaveState,
  usePinnedNews,
} from "@/src/services/api/news/news.hooks";
import { useToggleSaved } from "@/src/services/profile/profile.hooks";
import { useRouter } from "expo-router";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, Text, useWindowDimensions, View } from "react-native";
import NewsCard from "./news-card";
import NewsCarouselDots from "./news-carousel-dots";
import { useAutoCarousel } from "./use-auto-carousel";

const MAX_FEATURED_NEWS = 6;
const CARD_GAP = 12;

type FeaturedNewsProps = Readonly<{
  groupId?: string;
}>;
const FeaturedNewsCarousel = ({groupId}:FeaturedNewsProps) => {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const cardWidth = isMobile ? 200 : 256;
  const cardHeight = isMobile ? 128 : 160;
  const cardStep = cardWidth + CARD_GAP;

  const { user } = useAuth();
  const userId = user?.itemId ?? "";
  const { isSaved } = useNewsSaveState();
  const { mutate: toggleSave } = useToggleSaved(userId, "news");

  const { data: pinnedNews = [], isLoading } = usePinnedNews(groupId);
  const { data: categoriesResult } = useGetCategoriesQuery({
    tag: null,
    pageSize: 1000,
  });
  const scrollRef = useRef<ScrollView>(null);
  const { t } = useTranslation();
  const router = useRouter();

  const categoryMap = new Map<string, string>(
    (categoriesResult?.items ?? []).map((c) => [c.ItemId, c.categoryName]),
  );

  const news = pinnedNews.slice(0, MAX_FEATURED_NEWS);

  const activeIndex = useAutoCarousel(scrollRef, news.length, cardStep);

  return (
    <View className="md:rounded-2xl md:border md:border-ash-3 md:bg-chalk md:p-4">
      <Text className="text-sm font-bold mb-3 text-onyx">
        {t("LABEL_PINNED_NEWS")}
      </Text>

      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        snapToInterval={cardStep}
        decelerationRate="fast"
        contentContainerStyle={{ flexGrow: 1 }}
      >
        {isLoading
          ? Array.from({ length: 3 }).map((_, idx) => (
              <View
                key={idx}
                style={{
                  width: cardWidth,
                  height: cardHeight,
                  marginRight: CARD_GAP,
                }}
                className="rounded-2xl bg-ash-3 animate-pulse"
              />
            ))
          : null}
        {!isLoading && news.length === 0 ? (
          <View className="flex-1 items-center justify-center py-6">
            <Text className="text-sm text-ash-1">
              {t("LABEL_NO_PINNED_NEWS")}
            </Text>
          </View>
        ) : null}
        {news.map((item) => (
          <NewsCard
            key={item.ItemId}
            item={item}
            categoryMap={categoryMap}
            cardWidth={cardWidth}
            cardHeight={cardHeight}
            isSaved={isSaved(item.ItemId)}
            onSave={() => toggleSave(item.ItemId)}
            onPress={() =>
              router.push({
                pathname: "/(tabs)/news/news-detail",
                params: { newsId: item.ItemId },
              })
            }
          />
        ))}
      </ScrollView>

      <NewsCarouselDots count={news.length} activeIndex={activeIndex} />
    </View>
  );
};

export default FeaturedNewsCarousel;
